import { Suspense } from 'react';
import { createBrowserRouter, Navigate, Outlet, RouterProvider, ScrollRestoration } from 'react-router-dom';
import { CurrencyProvider } from '@/contexts/CurrencyContext';
import enterpriseRouteConfig from '@/enterprise/Router';
import { FeaturesProvider } from '@/lib/features';
import Hydrate from '@/lib/Hydrate';
import TokenGuard from '@/lib/TokenGuard';
import { DataDogProvider } from '@/reporting/DataDog';
import ArtifactsGate from '@/shared/components/ArtifactsGate';
import ExperienceRedirect from '@/shared/components/ExperienceRedirect';
import FullPageLoader from '@/shared/components/FullPageLoader';
import InactivityMonitor from '@/shared/components/InactivityMonitor';
import AuthImpersonation from '@/shared/pages/AuthImpersonation';
import AuthMagicLogin from '@/shared/pages/AuthMagicLogin';
import PageNotFound from '@/shared/pages/PageNotFound';
import SomethingsWrong from '@/shared/pages/SomethingsWrong';
import { AccountFeatureConfigsProvider } from '@/shared/providers/AccountFeatureConfigsContext';
import Intercom from './reporting/Intercom';
import ImpersonationMonitor from './shared/components/ImpersonationMonitor';
import { NavigationProvider } from './shared/components/SideNavigation/NavigationContext';

const browserRouter = createBrowserRouter([
  {
    children: [
      // unauthenticated routes
      {
        path: 'enterprise/auth',
        children: [
          {
            index: true,
            element: <ExperienceRedirect />,
          },
          {
            path: 'magic-login',
            element: <AuthMagicLogin />,
          },
          {
            path: 'impersonate',
            element: <AuthImpersonation />,
          },
          {
            path: '*',
            element: <ExperienceRedirect />,
          },
        ],
      },
      { path: 'enterprise/page-not-found', element: <PageNotFound /> },
      // authenticated routes
      {
        errorElement: <SomethingsWrong />,
        element: (
          <Suspense fallback={<FullPageLoader />}>
            <TokenGuard>
              <Hydrate>
                <AccountFeatureConfigsProvider>
                  <FeaturesProvider>
                    <DataDogProvider>
                      <ArtifactsGate>
                        <NavigationProvider>
                          <CurrencyProvider>
                            <ImpersonationMonitor />
                            <InactivityMonitor />
                            <ScrollRestoration />
                            <Intercom />
                            <Outlet />
                          </CurrencyProvider>
                        </NavigationProvider>
                      </ArtifactsGate>
                    </DataDogProvider>
                  </FeaturesProvider>
                </AccountFeatureConfigsProvider>
              </Hydrate>
            </TokenGuard>
          </Suspense>
        ),
        children: [
          {
            path: '/',
            element: <ExperienceRedirect />,
          },
          {
            path: '/enterprise',
            element: <ExperienceRedirect />,
          },
          // this handles things like /supplier/login which search engines can direct to
          {
            path: '/supplier/*',
            element: <ExperienceRedirect />,
          },
          enterpriseRouteConfig,
          { path: '*', element: <Navigate to="enterprise/page-not-found" /> },
        ],
      },
    ],
  },
]);

const Router = () => {
  return <RouterProvider router={browserRouter} />;
};

export default Router;
